import { useId } from 'react';

import {
	FASTSHIFT_ASSESSMENT_BLOCKER_STATUSES,
	FASTSHIFT_ASSESSMENT_RISKS,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { IconTile } from '@atlaskit/icon';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import MinusIcon from '@atlaskit/icon/core/minus';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Hide, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { AssessmentResultProgress } from './assessment-result-progress';
import { messages } from './messages';

import type { AssessmentResultCardViewModel } from './map-assessment-result-card-view-model';
import type {
	FastshiftAssessmentBlockerStatus,
	FastshiftAssessmentRisk,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import type { MessageDescriptor } from 'react-intl';

const styles = cssMap({
	listItem: {
		listStyleType: 'none',
	},
	resultRow: {
		boxSizing: 'border-box',
		width: '100%',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
		textAlign: 'start',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	resultRowSelected: {
		boxSizing: 'border-box',
		width: '100%',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderInlineStartWidth: token('border.width.selected'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border.selected'),
		borderRadius: token('radius.large'),
		color: token('color.text'),
		textAlign: 'start',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtlest.pressed'),
		},
	},
	resultContent: {
		minWidth: '0',
		width: '100%',
	},
	resultIdentity: {
		minWidth: '0',
	},
	resultMain: {
		minWidth: '0',
	},
	resultSummary: {
		minWidth: '0',
		width: '100%',
	},
	resultDetails: {
		minWidth: '0',
		overflow: 'hidden',
	},
	resultValue: {
		flexShrink: '0',
	},
	valueTag: {
		display: 'inline-flex',
		alignItems: 'center',
		minWidth: '0',
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
	},
	wrappingText: {
		overflowWrap: 'anywhere',
	},
});

const getStatusMessage = (status: FastshiftAssessmentRisk | null): MessageDescriptor => {
	if (status === FASTSHIFT_ASSESSMENT_RISKS.LOW_RISK) {
		return messages.passedStatus;
	}

	if (status === FASTSHIFT_ASSESSMENT_RISKS.SKIPPED) {
		return messages.skippedStatus;
	}

	if (
		status === FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK ||
		status === FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK
	) {
		return messages.needsAttentionStatus;
	}

	return messages.unavailableStatus;
};

const NEEDS_ATTENTION_RISKS: ReadonlySet<FastshiftAssessmentRisk> = new Set([
	FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK,
	FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK,
]);

const STATUS_ICON_CONFIGS = {
	[FASTSHIFT_ASSESSMENT_RISKS.LOW_RISK]: {
		icon: StatusSuccessIcon,
		appearance: 'lime',
		testId: 'assessment-result-icon-success',
	},
	[FASTSHIFT_ASSESSMENT_RISKS.SKIPPED]: {
		icon: MinusIcon,
		appearance: 'gray',
		testId: 'assessment-result-icon-skipped',
	},
	[FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK]: {
		icon: StatusWarningIcon,
		appearance: 'orange',
		testId: 'assessment-result-icon-warning',
	},
	[FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK]: {
		icon: StatusWarningIcon,
		appearance: 'orange',
		testId: 'assessment-result-icon-warning',
	},
} as const;

const UNAVAILABLE_ICON_CONFIG = {
	icon: MinusIcon,
	appearance: 'gray',
	testId: 'assessment-result-icon-unavailable',
} as const;

const BLOCKER_ICON_CONFIG = {
	icon: StatusErrorIcon,
	appearance: 'red',
	testId: 'assessment-result-icon-blocker',
} as const;

const getStatusIconConfig = (
	status: FastshiftAssessmentRisk | null,
	blockerStatus: FastshiftAssessmentBlockerStatus,
) =>
	blockerStatus === FASTSHIFT_ASSESSMENT_BLOCKER_STATUSES.BLOCKER &&
	status !== null &&
	NEEDS_ATTENTION_RISKS.has(status)
		? BLOCKER_ICON_CONFIG
		: (status && STATUS_ICON_CONFIGS[status]) || UNAVAILABLE_ICON_CONFIG;

const getChevronColor = (isSelected: boolean) =>
	isSelected ? token('color.icon.selected') : undefined;

export const AssessmentResultStatusIcon = ({
	status,
	blockerStatus,
}: {
	status: FastshiftAssessmentRisk | null;
	blockerStatus: FastshiftAssessmentBlockerStatus;
}) => {
	const config = getStatusIconConfig(status, blockerStatus);

	return (
		<IconTile
			size="medium"
			label=""
			icon={config.icon}
			appearance={config.appearance}
			testId={config.testId}
		/>
	);
};

export type AssessmentResultCardProps = AssessmentResultCardViewModel & {
	isSelected?: boolean;
	onSelect: () => void;
};

export const AssessmentResultCard = ({
	id,
	dataPoint,
	dataPointSummary,
	valueText,
	status,
	blockerStatus,
	hasRecommendations,
	progress,
	isSelected = false,
	onSelect,
}: AssessmentResultCardProps) => {
	const { formatMessage } = useIntl();
	const generatedId = useId();
	const statusLabel = formatMessage(getStatusMessage(status));
	const accessibleDescription = formatMessage(
		hasRecommendations ? messages.resultStatusWithRecommendation : messages.resultStatus,
		{ status: statusLabel },
	);
	const resultId = `${generatedId}-result-${id}`;
	const dataPointId = `${resultId}-data-point`;
	const dataPointSummaryId = `${resultId}-data-point-summary`;
	const customerDataId = `${resultId}-customer-data`;
	const descriptionId = `${resultId}-description`;
	const accessibleNameIds = [
		dataPointId,
		dataPointSummary ? dataPointSummaryId : null,
		valueText ? customerDataId : null,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<Box as="li" xcss={styles.listItem}>
			<Pressable
				aria-labelledby={accessibleNameIds}
				aria-describedby={descriptionId}
				onClick={onSelect}
				xcss={isSelected ? styles.resultRowSelected : styles.resultRow}
			>
				<Inline alignBlock="center" space="space.200" xcss={styles.resultContent}>
					<Inline alignBlock="center" space="space.100" grow="fill" xcss={styles.resultIdentity}>
						<AssessmentResultStatusIcon status={status} blockerStatus={blockerStatus} />
						<Stack space="space.050" grow="fill" xcss={styles.resultMain}>
							<Inline
								spread="space-between"
								alignBlock="center"
								space="space.200"
								xcss={styles.resultSummary}
							>
								<Stack space="space.025" xcss={styles.resultDetails}>
									<Text id={dataPointId} weight="semibold" xcss={styles.wrappingText}>
										{dataPoint}
									</Text>
									{dataPointSummary && (
										<Text
											id={dataPointSummaryId}
											color="color.text.subtlest"
											size="small"
											xcss={styles.wrappingText}
										>
											{dataPointSummary}
										</Text>
									)}
								</Stack>
								<Inline as="span" alignBlock="center" space="space.100">
									{valueText && (
										<Hide below="lg" as="span" xcss={styles.resultValue}>
											<Box as="span" id={customerDataId}>
												{progress ? (
													<Text>{valueText}</Text>
												) : (
													<Box as="span" xcss={styles.valueTag}>
														<Text size="small">{valueText}</Text>
													</Box>
												)}
											</Box>
										</Hide>
									)}
								</Inline>
							</Inline>
							{progress && (
								<AssessmentResultProgress
									dataPoint={dataPoint}
									valueText={valueText}
									progress={progress}
								/>
							)}
						</Stack>
					</Inline>
					<ChevronRightIcon label="" size="small" color={getChevronColor(isSelected)} />
				</Inline>
				<VisuallyHidden id={descriptionId}>{accessibleDescription}</VisuallyHidden>
			</Pressable>
		</Box>
	);
};
